import { useEffect } from 'preact/hooks';
import { mediaUrl } from '../api';
import type { T } from '../i18n';
import { set, stampById } from '../state';
import type { AppState } from '../state';
import { countryLabel, countryName } from '../countries';
import { StampArt } from '../stamp';
import { BackArrow } from './Icons';

/**
 * One postcard, full screen. The card stays mounted while it turns over, so
 * switching sides runs the design's 3D flip instead of swapping faces.
 */
export function Reader({ s, t }: { s: AppState; t: T }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') set({ openCardId: null, cardSide: 'front' });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const card = s.mailbox.find((c) => c.id === s.openCardId);
  if (!card) return null;

  const stamp = stampById(card.stamp);
  const close = () => set({ openCardId: null, cardSide: 'front' });
  const flip = () => set({ cardSide: s.cardSide === 'front' ? 'back' : 'front' });

  return (
    <div class="reader" role="dialog" aria-modal="true" aria-label={`${card.place} · ${card.from}`}>
      <button class="reader-close" aria-label={t('close')} onClick={close}>
        <BackArrow size={30} />
      </button>

      <div class="flip-stage">
        <div
          class="flipper"
          data-side={s.cardSide}
          role="button"
          tabIndex={0}
          aria-label={t('flip')}
          onClick={flip}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              flip();
            }
          }}
        >
          <div class="face face-front" aria-hidden={s.cardSide === 'back' ? 'true' : undefined}>
            <div class="face-photo" data-photo={String(!!card.frontMedia)}>
              {card.frontMedia && <img src={mediaUrl(card.frontMedia)} alt="" />}
              <span class="face-caption">{card.frontLabel}</span>
            </div>
            <div class="face-foot">
              <span class="face-place">{card.place}</span>
              <span class="face-postmark">{card.postmark}</span>
            </div>
          </div>

          <div class="face face-back" aria-hidden={s.cardSide === 'front' ? 'true' : undefined}>
            <div class="back-note">
              <div class="back-label">{t('message')}</div>
              {card.noteMedia ? (
                <div class="back-scan">
                  <img src={mediaUrl(card.noteMedia)} alt={t('scanFrom', { who: card.from })} />
                </div>
              ) : (
                <div class="back-text">{card.noteText}</div>
              )}
            </div>
            <div class="back-addr">
              <div class="back-stamp-row">
                <div class="back-stamp">
                  {stamp && <StampArt stamp={stamp} />}
                  <div class="postmark" aria-hidden="true">
                    <span>{card.place}</span>
                    <span>{card.postmark}</span>
                    <span>{t('postmarkWord')}</span>
                  </div>
                </div>
              </div>
              <div>
                <div class="back-label">{t('to')}</div>
                <div class="back-name">{card.to}</div>
                <div class="back-line">{countryLabel(card.toCountry, s.lang)}</div>
                <div class="back-blank" />
                <div class="back-foot">qué fue, pana? · {countryName(card.fromCountry, s.lang)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
